import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { MessageCircle, ShieldCheck, ArrowLeft } from "lucide-react";
import { messagesAPI } from "../api";
import { useAuth } from "../context/AuthContext";
import Hint from "../components/Onboarding/Hint";
import "./Messages.css";

const Messages = () => {
  const { isAuthenticated, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationWithId = searchParams.get("user_id") ? parseInt(searchParams.get("user_id")) : null;
  const orderId = searchParams.get("order_id") ? parseInt(searchParams.get("order_id")) : null;
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadConversations = useCallback(async () => {
    try {
      const response = await messagesAPI.getConversations();
      setConversations(response.data);
      
      // Si un user_id est dans l'URL, sélectionner cette conversation
      if (conversationWithId) {
        const conv = response.data.find(c => c.user.id === conversationWithId);
        if (conv) {
          setSelectedConversation(conv.user);
        } else {
          // Si la conversation n'existe pas encore, charger les infos minimales de l'utilisateur
          try {
            const userResponse = await messagesAPI.getContact(conversationWithId);
            setSelectedConversation(userResponse.data);
          } catch (err) {
            console.error("Error loading user:", err);
            // Créer un objet minimal si l'utilisateur ne peut pas être chargé
            setSelectedConversation({ id: conversationWithId, name: "Utilisateur" });
            setError("Impossible de récupérer les informations de ce contact. Le nom affiché peut être incorrect.");
          }
        }
      }
    } catch (err) {
      console.error("Error loading conversations:", err);
      setError("Erreur lors du chargement des conversations");
    }
  }, [conversationWithId]);

  const loadMessages = useCallback(async (userId, orderIdParam = null) => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const params = { conversation_with: userId };
      if (orderIdParam) {
        params.order_id = orderIdParam;
      }
      const response = await messagesAPI.getAll(params);
      setMessages(response.data);
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error("Error loading messages:", err);
      setError("Erreur lors du chargement des messages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadConversations();
    }
  }, [isAuthenticated, loadConversations]);

  useEffect(() => {
    if (selectedConversation?.id) {
      loadMessages(selectedConversation.id, orderId);
    }
  }, [selectedConversation, orderId, loadMessages]);

  // Auto-refresh des messages toutes les 5 secondes
  useEffect(() => {
    if (!selectedConversation?.id) return;
    
    const interval = setInterval(() => {
      loadMessages(selectedConversation.id, orderId);
      loadConversations(); // Rafraîchir aussi la liste des conversations
    }, 5000);
    
    return () => clearInterval(interval);
  }, [selectedConversation, orderId, loadMessages, loadConversations]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation?.id || sending) return;

    setSending(true);
    setError("");
    
    try {
      const messageData = {
        receiver_id: selectedConversation.id,
        content: newMessage.trim(),
        order_id: orderId || null,
      };
      
      await messagesAPI.create(messageData);
      setNewMessage("");
      
      // Recharger les messages et conversations
      await loadMessages(selectedConversation.id, orderId);
      await loadConversations();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi du message");
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMessageDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSelectConversation = (conversationUser) => {
    setSelectedConversation(conversationUser);
    setSearchParams({ user_id: conversationUser.id, ...(orderId && { order_id: orderId }) });
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
    setSearchParams({});
  };

  if (!isAuthenticated) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">
          Vous devez être connecté pour accéder aux messages
        </div>
        <Link to="/login" className="btn btn-primary">
          Se connecter
        </Link>
      </div>
    );
  }

  return (
    <div className={`messages-container ${selectedConversation ? "conversation-open" : ""}`} data-tour="messages-page">
      <div className="messages-sidebar">
        <div className="messages-header">
          <h2>Messages</h2>
          {orderId && (
            <Link to={`/orders/${orderId}`} className="btn btn-outline btn-sm">
              Voir la commande
            </Link>
          )}
        </div>
        
        <div className="conversations-list">
          {conversations.length === 0 ? (
            <div className="empty-conversations">
              <div className="empty-state-icon"><MessageCircle size={56} strokeWidth={1.75} /></div>
              <p>Aucune conversation</p>
              <p className="text-muted">Vos conversations apparaîtront ici</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.user.id}
                className={`conversation-item ${
                  selectedConversation?.id === conv.user.id ? "active" : ""
                }`}
                onClick={() => handleSelectConversation(conv.user)}
              >
                <div className="conversation-avatar">
                  {conv.user.name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div className="conversation-info">
                  <div className="conversation-header">
                    <h4>{conv.user.name || "Utilisateur"}</h4>
                    {conv.last_message?.created_at && (
                      <span className="conversation-time">
                        {formatDate(conv.last_message.created_at)}
                      </span>
                    )}
                  </div>
                  <div className="conversation-preview">
                    <p className={conv.last_message?.is_from_me ? "sent" : ""}>
                      {conv.last_message?.content || "Aucun message"}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="unread-badge">{conv.unread_count}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="messages-chat">
        {!selectedConversation ? (
          <div className="chat-empty">
            <div className="empty-state-icon"><MessageCircle size={56} strokeWidth={1.75} /></div>
            <h3>Sélectionnez une conversation</h3>
            <p>Choisissez une conversation dans la liste pour commencer à échanger</p>
          </div>
        ) : (
          <>
            <div className="chat-header">
              <div className="chat-user-info">
                <button
                  type="button"
                  className="chat-back-btn"
                  onClick={handleBackToList}
                  aria-label="Retour aux conversations"
                >
                  <ArrowLeft size={22} />
                </button>
                <div className="chat-avatar">
                  {selectedConversation.name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div>
                  <h3>{selectedConversation.name || "Utilisateur"}</h3>
                  {selectedConversation.role && (
                    <p className="text-muted">
                      {selectedConversation.role === "breeder" ? "Éleveur" : "Restaurateur"}
                    </p>
                  )}
                </div>
              </div>
              {orderId && (
                <Link to={`/orders/${orderId}`} className="btn btn-outline btn-sm">
                  Voir la commande #{orderId}
                </Link>
              )}
            </div>

            <div style={{ padding: "0 1rem" }}>
              <Hint id="messages_no_contact_sharing" icon={ShieldCheck}>
                Ne partagez pas vos coordonnées personnelles ici. Les commandes payées sur PouletDirect
                sont protégées : le paiement n'est libéré qu'après confirmation de livraison par code OTP.
              </Hint>
            </div>

            <div className="chat-messages" ref={messagesContainerRef}>
              {loading && messages.length === 0 ? (
                <div className="loading-messages">
                  <div className="spinner"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="no-messages">
                  <p>Aucun message pour le moment</p>
                  <p className="text-muted">Envoyez le premier message !</p>
                </div>
              ) : (
                <>
                  {messages.map((message, index) => {
                    const isMyMessage = message.sender_id === user.id;
                    const showDate = index === 0 || 
                      new Date(message.created_at) - new Date(messages[index - 1].created_at) > 3600000; // 1 heure
                    
                    return (
                      <React.Fragment key={message.id}>
                        {showDate && (
                          <div className="message-date-divider">
                            {new Date(message.created_at).toLocaleDateString("fr-FR", {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                            })}
                          </div>
                        )}
                        <div className={`message ${isMyMessage ? "message-sent" : "message-received"}`}>
                          <div className="message-content">
                            <p>{message.content}</p>
                            <span className="message-time">
                              {formatMessageDate(message.created_at)}
                            </span>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {error && <div className="alert alert-error chat-error">{error}</div>}

            <form className="chat-input-form" onSubmit={handleSendMessage} data-tour="messages-chat-input">
              <input
                type="text"
                className="chat-input"
                placeholder="Tapez votre message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={sending}
              />
              <button
                type="submit"
                className="btn btn-primary chat-send-btn"
                disabled={!newMessage.trim() || sending}
              >
                {sending ? "..." : "Envoyer"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Messages;
